/**
 * JobProfitability — admin-only dashboard tab (Phase 1 of the job
 * profitability feature, 09 Feb 2026).
 *
 * Consumes GET /api/stock/analytics/job-profitability. Renders a
 * per-job cost/margin table + top-line summary + bottom-5 lowest
 * margin callout. Follows the same card/chart styling used by
 * StockAnalytics.jsx.
 *
 * Rows with incomplete cost data (missing pay_model / pay_rate /
 * material_cost) are VISUALLY distinguished with a "data incomplete"
 * badge and greyed-out numeric cells — this must never look like the
 * job made €0 margin. The backend already excludes these from summary
 * math; the UI's job is to make that distinction obvious to the reader.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Calendar,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  User,
  Wrench,
  Info,
  CheckCircle2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { API } from "../../App";
import JobCostDrawer from "./JobCostDrawer";
import { resolveVehicleModel } from "../../utils/vehicleDisplay";

const fmtEuro = (v) =>
  v === null || v === undefined ? "—" : `€${Number(v).toFixed(2)}`;
const fmtPct = (v) =>
  v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`;
// Duration formatter — hours+minutes, consistent with how DashboardPage
// and StaffScheduleView render times ("1h 45m", "45m", "2h"). We always
// floor to whole minutes; sub-minute precision is noise for a job that
// took an hour and change.
const fmtDuration = (v) => {
  if (v === null || v === undefined) return "—";
  const mins = Math.max(0, Math.floor(Number(v)));
  if (Number.isNaN(mins)) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Human-readable pay model labels — matches the Add/Edit Staff dropdown.
const PAY_MODEL_LABEL = {
  hourly: "Hourly",
  pct_gross: "% Gross",
  pct_net: "% Net",
};

const JobProfitability = () => {
  const [timeRange, setTimeRange] = useState("30");
  const [serviceId, setServiceId] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [drawerLogId, setDrawerLogId] = useState(null);
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [staff, setStaff] = useState([]);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  // Set of booking_ids that currently have a live duration_alert. The
  // backend (services/duration_alert.py) is the single source of truth
  // for "what counts as slow" — we never recompute a threshold here,
  // just mirror what the alert engine already flagged. Empty on any
  // fetch failure — silent so a flaky /duration-alerts call never
  // hides the table.
  const [flaggedDurationBookings, setFlaggedDurationBookings] = useState(new Set());

  // --- Backfill state ---
  // Two-tap flow, mirrors the "Backfill from Job Logs" button in
  // StockAnalytics.jsx exactly:
  //   1) Tap "Backfill historical margins" → dry_run=true, dialog opens
  //      showing scanned/to_update/already/no_pay/no_matcost breakdown
  //   2) Tap "Confirm & run backfill" → dry_run=false, writes and
  //      refreshes the profitability table + summary cards in place
  // A dry-run of 0 to_update after we've seen at least one plan hides
  // the confirm button and shows a "nothing to backfill" state instead —
  // never lets the button look clickable-but-useless on repeat visits.
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillPlan, setBackfillPlan] = useState(null);
  const [backfillResult, setBackfillResult] = useState(null);
  // Cached from the most recent dry-run so the tile face can hide the
  // button between renders if there's genuinely nothing left to do.
  const [lastPlanToUpdate, setLastPlanToUpdate] = useState(null);
  // Force mode — recompute jobs that already carry a margin (see
  // openBackfillPreview). Off by default so the normal flow is unchanged.
  const [backfillForce, setBackfillForce] = useState(false);

  // --- Bulk "no stock materials used" ---------------------------------------
  // Same two-tap discipline as the backfill: the first tap runs
  // dry_run=true and the dialog lists every job by name before anything
  // is written.
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPlan, setBulkPlan] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  // Plain-English P&L effect of the last save (drawer or bulk).
  const [impact, setImpact] = useState(null);

  const toggleSelected = (logId) =>
    setSelectedLogIds((s) =>
      s.includes(logId) ? s.filter((x) => x !== logId) : [...s, logId]);

  const runBulk = async (dryRun) => {
    setBulkBusy(true);
    if (dryRun) { setBulkPlan(null); setBulkResult(null); setBulkOpen(true); }
    try {
      const res = await axios.post(
        `${API}/job-logs/admin/bulk-confirm-no-materials`,
        { log_ids: selectedLogIds, dry_run: dryRun },
      );
      if (dryRun) {
        setBulkPlan(res.data);
      } else {
        setBulkResult(res.data);
        setImpact(res.data.impact || []);
        setSelectedLogIds([]);
        toast.success(`${res.data.applied} job${res.data.applied === 1 ? "" : "s"} confirmed as using no stock materials`);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk confirm failed");
      if (dryRun) setBulkOpen(false);
    } finally {
      setBulkBusy(false);
    }
  };

  // Load filter option lists once. Both endpoints are admin-only, but
  // this tab is only rendered for admins in the first place.
  useEffect(() => {
    (async () => {
      try {
        const [svc, st] = await Promise.all([
          axios.get(`${API}/services`).catch(() => ({ data: [] })),
          axios.get(`${API}/auth/staff`).catch(() => ({ data: [] })),
        ]);
        setServices(svc.data || []);
        setStaff((st.data || []).filter((s) => s.is_active !== false));
      } catch {
        // non-fatal; user just gets empty filter dropdowns
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: Number(timeRange) };
      if (serviceId) params.service_id = serviceId;
      if (technicianId) params.technician_id = technicianId;
      // Fetch profitability + live duration alerts in parallel.
      // Duration-alerts pattern mirrors JobAlertsSection.jsx's fetch —
      // same endpoint, same acknowledged=false default. We only want
      // ACTIVE flags: an acknowledged alert is one the admin has
      // already reviewed, so quietly de-flagging that row in the table
      // matches the mental model.
      const [profitRes, durRes] = await Promise.all([
        axios.get(`${API}/stock/analytics/job-profitability`, { params }),
        axios
          .get(`${API}/stock/duration-alerts`, { params: { acknowledged: false } })
          .catch(() => ({ data: { alerts: [] } })),
      ]);
      setData(profitRes.data);
      // Build the Set once so the row render loop is O(1) per row.
      // Filter to the same date window as the profitability query —
      // the alerts endpoint doesn't accept a `days` param, so we clip
      // client-side by comparing created_at against the window start.
      const cutoff =
        Date.now() - Number(timeRange) * 24 * 60 * 60 * 1000;
      const flagged = new Set(
        (durRes.data?.alerts || [])
          .filter((a) => {
            if (!a.booking_id) return false;
            if (!a.created_at) return true; // no timestamp → assume in-window
            const ts = new Date(a.created_at).getTime();
            return Number.isFinite(ts) ? ts >= cutoff : true;
          })
          .map((a) => a.booking_id),
      );
      setFlaggedDurationBookings(flagged);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to load job profitability",
      );
    } finally {
      setLoading(false);
    }
  }, [timeRange, serviceId, technicianId]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Backfill handlers -----------------------------------------------------
  // `force` re-aggregates jobs that ALREADY have a margin stored. Needed
  // whenever a historical material cost gets corrected: the line item
  // changes but the job's stored margin doesn't, and the normal backfill
  // skips any job with a margin already present.
  // Step 1 — hard-code dry_run=true so a click can never mutate data.
  const openBackfillPreview = async (force = backfillForce) => {
    setBackfillLoading(true);
    setBackfillPlan(null);
    setBackfillResult(null);
    setBackfillOpen(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-job-profitability?dry_run=true&force=${force}`,
      );
      setBackfillPlan(res.data);
      setLastPlanToUpdate(res.data.to_update ?? 0);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Backfill preview failed: ${detail}`);
      setBackfillOpen(false);
    } finally {
      setBackfillLoading(false);
    }
  };

  const toggleBackfillForce = async () => {
    const next = !backfillForce;
    setBackfillForce(next);
    await openBackfillPreview(next);
  };

  // Step 2 — real mutation. Only enabled after the dry-run summary is
  // visible AND to_update > 0. Server-side is idempotent so an accidental
  // double-tap is safe, but the client still guards to keep the UX honest.
  const confirmBackfill = async () => {
    if (!backfillPlan) return;
    setBackfillLoading(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-job-profitability?dry_run=false&force=${backfillForce}`,
      );
      setBackfillResult(res.data);
      setLastPlanToUpdate(0); // done — hide the CTA on the tile face
      toast.success(
        `Backfill complete — ${res.data.updated} job${res.data.updated === 1 ? "" : "s"} updated`,
      );
      // Refresh the profitability table + summary so the newly-computed
      // rows land immediately without a manual reload.
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Backfill failed: ${detail}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  const closeBackfill = () => {
    if (backfillLoading) return; // don't allow closing mid-mutation
    setBackfillOpen(false);
    setBackfillPlan(null);
    setBackfillResult(null);
  };

  // A row counts as "complete" the same way the backend defines it —
  // ALL four numbers present. Anything else is greyed out.
  const isComplete = (r) =>
    r.material_cost !== null &&
    r.material_cost !== undefined &&
    r.labour_cost !== null &&
    r.labour_cost !== undefined &&
    r.total_job_cost !== null &&
    r.total_job_cost !== undefined &&
    r.price_charged !== null &&
    r.price_charged !== undefined &&
    r.net_revenue !== null &&
    r.net_revenue !== undefined;

  const rowsSorted = useMemo(() => {
    if (!data?.rows) return [];
    // The "N jobs excluded" banner links here: show only the jobs whose
    // costs actually need fixing.
    const rows = incompleteOnly
      ? data.rows.filter((r) => !isComplete(r))
      : [...data.rows];
    const { key, dir } = sort;
    rows.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      // Nulls always sink to the bottom regardless of direction — an
      // "incomplete" row shouldn't compete with real numbers.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return dir === "asc" ? av - bv : bv - av;
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sort, incompleteOnly]);

  const bulkEligible = useMemo(
    () => rowsSorted.filter(
      (r) => r.log_id && !isComplete(r) && !(r.material_line_count > 0)
             && !r.materials_confirmed_none),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsSorted],
  );

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" },
    );

  const renderSortHeader = (label, sortKey, align = "left") => (
    <th
      onClick={() => toggleSort(sortKey)}
      className={`px-3 py-2 text-xs font-semibold text-slate-400 uppercase cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"}`}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === sortKey && (
          <span className="text-saturn-orange">
            {sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          </span>
        )}
      </span>
    </th>
  );

  const summary = data?.summary || {};

  // Avg duration — computed client-side from row-level duration_minutes
  // (skip nulls so missing durations don't drag the mean toward zero).
  // Same null-safety convention as the rest of this tab: absent data is
  // absent, never coerced to 0. useMemo so we don't recalc on every
  // parent re-render.
  const avgDurationMinutes = useMemo(() => {
    const durations = (data?.rows || [])
      .map((r) => r.duration_minutes)
      .filter((v) => v !== null && v !== undefined && Number.isFinite(Number(v)));
    if (durations.length === 0) return null;
    const sum = durations.reduce((acc, v) => acc + Number(v), 0);
    return sum / durations.length;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="job-profitability-tab">
      {/* --- Header + filters ---------------------------------------------------- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-saturn-orange" />
            Job Profitability
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Per-job cost, margin, and technician contribution — reads real
            duration &amp; material cost from job logs.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lastPlanToUpdate !== 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBackfillPreview(backfillForce)}
              disabled={backfillLoading}
              className="border-slate-700 text-slate-300 hover:text-white"
              data-testid="jp-backfill-btn"
              title="Retroactively compute labour_cost / margin on historical job_logs — dry-run first, safe to re-run."
            >
              <Wrench className="w-4 h-4 mr-2" />
              Backfill historical margins
            </Button>
          )}
          <Select value={serviceId || "__all"} onValueChange={(v) => setServiceId(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white" data-testid="filter-service">
              <Wrench className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All services</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.service_id} value={s.service_id}>
                  {s.name || s.service_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={technicianId || "__all"}
            onValueChange={(v) => setTechnicianId(v === "__all" ? "" : v)}
          >
            <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white" data-testid="filter-technician">
              <User className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Technician" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All technicians</SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.user_id} value={s.user_id}>
                  {s.name || s.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white" data-testid="filter-range">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* --- Summary cards ------------------------------------------------------- */}
      {/* Bumped from md:grid-cols-5 → md:grid-cols-6 to fit the new
          "Effective revenue" tile beside "Total revenue" without
          shrinking the existing cards. The tile shows what actually
          landed in the till after any promo discount; the small
          "€N given to promos" line below the value quantifies the
          gap so an admin can see at a glance whether a campaign is
          paying for itself. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryCard
          label="Avg margin %"
          value={fmtPct(summary.avg_margin_pct)}
          tone="amber"
          testId="summary-avg-margin"
          hint="Measured on net (ex-VAT) revenue"
        />
        <SummaryCard
          label="Avg duration"
          value={fmtDuration(avgDurationMinutes)}
          testId="summary-avg-duration"
        />
        <SummaryCard
          label="Total revenue (ex-VAT)"
          value={fmtEuro(summary.total_revenue)}
          testId="summary-revenue"
          hint={
            summary.total_revenue_gross != null
              ? `€${Number(summary.total_revenue_gross).toFixed(2)} gross incl. €${Number(summary.total_vat_collected || 0).toFixed(2)} VAT owed to Revenue`
              : null
          }
        />
        <EffectiveRevenueCard
          effective={summary.effective_revenue}
          gap={summary.promo_discount_given}
        />
        <SummaryCard
          label="Total cost"
          value={fmtEuro(summary.total_cost)}
          testId="summary-cost"
        />
        <SummaryCard
          label="Total margin"
          value={fmtEuro(summary.total_margin)}
          tone="emerald"
          testId="summary-margin"
          hint="Net revenue minus cost — VAT excluded"
        />
      </div>

      {/* --- Data-completeness callout ------------------------------------------ */}
      {summary.incomplete_cost_data > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-3 text-sm">
          <Info className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="text-amber-100">
            <div className="font-semibold">
              {summary.incomplete_cost_data} job{summary.incomplete_cost_data === 1 ? "" : "s"} excluded from averages
            </div>
            <div className="text-amber-200/80 text-xs mt-0.5">
              Missing pay_model / pay_rate for the technician, or no material cost recorded.
              Click a job row to add the missing costs without leaving this page.
            </div>
            <button
              onClick={() => setIncompleteOnly((v) => !v)}
              className="text-xs underline text-amber-200 hover:text-white mt-1"
              data-testid="jp-incomplete-filter-link"
            >
              {incompleteOnly ? "Show all jobs" : "Show only these jobs"}
            </button>
          </div>
        </div>
      )}

      {/* --- Bottom-5 lowest margin --------------------------------------------- */}
      {summary.bottom_5_lowest_margin?.length > 0 && (
        <Card className="bg-slate-900 border-rose-500/30" data-testid="bottom-5-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              Lowest margin jobs in this window
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {summary.bottom_5_lowest_margin.map((r) => (
                <div
                  key={r.booking_id}
                  className="flex justify-between items-center text-sm border-b border-slate-800 pb-1.5 last:border-0"
                  data-testid={`bottom-row-${r.booking_id}`}
                >
                  <div className="min-w-0 flex-1 truncate">
                    <span className="text-white">{r.customer_name || "—"}</span>
                    <span className="text-slate-500 text-xs ml-2">
                      {r.technician_name || "no tech"} · {r.booking_id}
                    </span>
                  </div>
                  <div className="text-right shrink-0 pl-2">
                    <div className="text-rose-300 font-semibold tabular-nums">
                      {fmtPct(r.margin_pct)}
                    </div>
                    <div className="text-slate-500 text-xs">
                      {fmtEuro(r.margin)} of {fmtEuro(r.net_revenue != null ? r.net_revenue : r.price_charged)} ex-VAT
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* --- Impact of the last cost edit --------------------------------------- */}
      {impact?.length > 0 && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm space-y-1"
             data-testid="jp-impact-banner">
          {impact.map((i) => (
            <p key={i.month} className="text-emerald-100">{i.message}</p>
          ))}
          <button onClick={() => setImpact(null)}
                  className="text-xs underline text-emerald-200/80 hover:text-white"
                  data-testid="jp-impact-dismiss">
            Dismiss
          </button>
        </div>
      )}

      {/* --- Bulk action bar ----------------------------------------------------- */}
      {selectedLogIds.length > 0 && (
        <div className="rounded-lg border border-saturn-orange/40 bg-saturn-orange/10 p-3 flex flex-wrap items-center gap-3 text-sm"
             data-testid="jp-bulk-bar">
          <span className="text-white font-semibold" data-testid="jp-bulk-count">
            {selectedLogIds.length} job{selectedLogIds.length === 1 ? "" : "s"} selected
          </span>
          <Button size="sm" onClick={() => runBulk(true)} disabled={bulkBusy}
                  className="bg-saturn-orange text-black hover:bg-saturn-orange/80"
                  data-testid="jp-bulk-no-materials-btn">
            No stock materials used
          </Button>
          <button onClick={() => setSelectedLogIds([])}
                  className="text-xs underline text-slate-300 hover:text-white"
                  data-testid="jp-bulk-clear">
            Clear selection
          </button>
        </div>
      )}

      {/* --- Per-job table ------------------------------------------------------- */}
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">
            Jobs ({data?.rows?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
            </div>
          ) : rowsSorted.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              No completed jobs in this window match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/50 border-b border-slate-700">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        title="Select every job below that has no material lines"
                        data-testid="jp-select-all"
                        checked={
                          bulkEligible.length > 0 &&
                          bulkEligible.every((r) => selectedLogIds.includes(r.log_id))
                        }
                        onChange={(e) =>
                          setSelectedLogIds(
                            e.target.checked ? bulkEligible.map((r) => r.log_id) : [],
                          )
                        }
                      />
                    </th>
                    {renderSortHeader("Customer", "customer_name")}
                    {renderSortHeader("Vehicle", "vehicle")}
                    {renderSortHeader("Service", "service_names")}
                    {renderSortHeader("Technician", "technician_name")}
                    {renderSortHeader("Materials", "material_cost", "right")}
                    {renderSortHeader("Duration", "duration_minutes", "right")}
                    {renderSortHeader("Labour", "labour_cost", "right")}
                    {renderSortHeader("Cost", "total_job_cost", "right")}
                    {renderSortHeader("Price (ex-VAT)", "net_revenue", "right")}
                    {renderSortHeader("Margin", "margin", "right")}
                    {renderSortHeader("%", "margin_pct", "right")}
                  </tr>
                </thead>
                <tbody>
                  {rowsSorted.map((r) => {
                    const complete = isComplete(r);
                    return (
                      <tr
                        key={r.booking_id}
                        onClick={() => r.log_id && setDrawerLogId(r.log_id)}
                        className={`border-b border-slate-800 hover:bg-slate-800/40 ${r.log_id ? "cursor-pointer" : ""} ${complete ? "" : "text-slate-500 bg-slate-950/40"}`}
                        title={r.log_id ? "Fix this job's costs" : undefined}
                        data-testid={`profit-row-${r.booking_id}`}
                      >
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          {!complete && r.log_id && !(r.material_line_count > 0) && (
                            <input
                              type="checkbox"
                              checked={selectedLogIds.includes(r.log_id)}
                              onChange={() => toggleSelected(r.log_id)}
                              title="Select for 'No stock materials used'"
                              data-testid={`jp-select-${r.booking_id}`}
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.customer_name || <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.vehicle?.make || "—"} {resolveVehicleModel(r) || ""}
                          {r.vehicle?.size && (
                            <span className="text-slate-500 text-xs ml-1">({r.vehicle.size})</span>
                          )}
                        </td>
                        <td className="px-3 py-2 max-w-[220px] truncate" title={(r.service_names || []).join(", ")}>
                          {(r.service_names || []).join(", ") || <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.technician_name || <span className="text-slate-600">unassigned</span>}
                          {r.pay_model && (
                            <span className="ml-1.5 text-[10px] uppercase text-slate-500">
                              · {PAY_MODEL_LABEL[r.pay_model] || r.pay_model}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(r.material_cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {(() => {
                            // Percentage-based pay models don't consume
                            // duration in their labour cost formula — but
                            // showing it is still useful for spotting jobs
                            // that ran longer than the crew's typical
                            // norms. Muted style + Info tooltip makes the
                            // "informational only" nature obvious without
                            // taking up column space.
                            const isInfoOnly =
                              r.pay_model === "pct_gross" || r.pay_model === "pct_net";
                            if (r.duration_minutes === null || r.duration_minutes === undefined) {
                              return <span className="text-slate-600">—</span>;
                            }
                            // Highlight if this booking is on the live
                            // duration-alerts list. Backend is the sole
                            // authority for "slow" — the client never
                            // recomputes the median × 1.5x threshold.
                            const isSlow = flaggedDurationBookings.has(r.booking_id);
                            const tooltip = isSlow
                              ? "Flagged: ran longer than typical for this service + vehicle size"
                              : isInfoOnly
                                ? "Informational — doesn't affect % of Gross/Net pay"
                                : undefined;
                            const wrapperClass = isSlow
                              ? "inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200 font-semibold"
                              : `inline-flex items-center gap-1 ${isInfoOnly ? "text-slate-400" : ""}`;
                            return (
                              <span
                                className={wrapperClass}
                                title={tooltip}
                                data-testid={
                                  isSlow
                                    ? `duration-slow-${r.booking_id}`
                                    : isInfoOnly
                                      ? `duration-info-${r.booking_id}`
                                      : undefined
                                }
                              >
                                {isSlow && (
                                  <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                                )}
                                {fmtDuration(r.duration_minutes)}
                                {!isSlow && isInfoOnly && (
                                  <Info className="w-3 h-3 opacity-60" aria-hidden="true" />
                                )}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(r.labour_cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(r.total_job_cost)}</td>
                        <td
                          className="px-3 py-2 text-right tabular-nums text-slate-300"
                          title={
                            r.net_revenue != null
                              ? `${fmtEuro(r.price_charged)} gross incl. ${fmtEuro(r.vat_amount)} VAT`
                              : undefined
                          }
                          data-testid={`jp-row-net-price-${r.booking_id}`}
                        >
                          {fmtEuro(r.net_revenue != null ? r.net_revenue : r.price_charged)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${complete ? (r.margin >= 0 ? "text-emerald-300" : "text-rose-300") : ""}`}>
                          {fmtEuro(r.margin)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums ${complete ? "" : ""}`}>
                          {complete ? (
                            fmtPct(r.margin_pct)
                          ) : (
                            <span
                              className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400"
                              data-testid="incomplete-badge"
                              title="Missing pay_model / pay_rate for the technician, or no material cost recorded."
                            >
                              data incomplete
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* --- Backfill dialog ---------------------------------------------
          Two-tap flow mirrors StockAnalytics.jsx: first tap runs
          dry_run=true and shows the plan; only "Confirm & run backfill"
          fires a real mutation. Never writes on the initial click. ----*/}
      <Dialog open={backfillOpen} onOpenChange={(v) => { if (!v) closeBackfill(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="jp-backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-400" />
              Backfill Historical Margins
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Recompute labour_cost and margin for older jobs that were
              completed before this feature existed, or before the
              technician had a pay rate configured. Safe, idempotent,
              dry-runs first — nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {backfillLoading && !backfillPlan && (
            <div className="py-6 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-slate-400 text-sm">Scanning job logs…</p>
            </div>
          )}

          {backfillPlan && !backfillResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Plan (dry-run, nothing written yet)
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-amber-200/80">Job logs scanned</span>
                  <span className="text-right font-mono" data-testid="jp-backfill-plan-scanned">{backfillPlan.scanned_job_logs}</span>
                  <span className="text-amber-200/80">Will update</span>
                  <span className="text-right font-mono font-bold text-amber-100" data-testid="jp-backfill-plan-to-update">{backfillPlan.to_update}</span>
                  <span className="text-amber-200/80">Already computed (skipped)</span>
                  <span className="text-right font-mono" data-testid="jp-backfill-plan-already">{backfillPlan.already_computed}</span>
                  <span className="text-amber-200/80">Skipped — no pay rate</span>
                  <span className="text-right font-mono" data-testid="jp-backfill-plan-nopay">{backfillPlan.skipped_no_pay_rate}</span>
                  <span className="text-amber-200/80">Skipped — no material cost</span>
                  <span className="text-right font-mono" data-testid="jp-backfill-plan-nomat">{backfillPlan.skipped_no_material_cost}</span>
                </div>
              </div>

              {/* Force mode — the normal backfill only fills in MISSING
                  margins, so a job whose material cost was corrected
                  after the fact never gets revisited. This recomputes
                  every job from its current line items. */}
              <label
                className="flex items-start gap-2 rounded border border-slate-700 bg-slate-800/50 p-3 text-xs text-slate-300 cursor-pointer"
                data-testid="jp-backfill-force-row"
              >
                <input
                  type="checkbox"
                  checked={backfillForce}
                  onChange={toggleBackfillForce}
                  disabled={backfillLoading}
                  className="mt-0.5 accent-amber-500"
                  data-testid="jp-backfill-force"
                />
                <span>
                  <span className="font-semibold text-amber-200">
                    Force recompute jobs that already have a margin
                  </span>
                  <span className="block text-slate-400 mt-0.5">
                    Use this after correcting a historical material cost — otherwise those
                    jobs count as already computed and get skipped.
                  </span>
                </span>
              </label>

              {/* No-pay-rate nudge — link admin to Staff Management so
                  they understand why some rows don't get backfilled
                  rather than assuming the tool is broken. */}
              {backfillPlan.skipped_no_pay_rate > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3 text-xs text-slate-300 flex gap-2 items-start" data-testid="jp-backfill-nopay-nudge">
                  <Info className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-amber-200">
                      {backfillPlan.skipped_no_pay_rate} job{backfillPlan.skipped_no_pay_rate === 1 ? "" : "s"} skipped — technician has no pay rate
                    </div>
                    <div className="text-slate-400 mt-0.5">
                      Set pay_model + pay_rate on the staff row in{" "}
                      <span className="text-amber-200">Staff Management</span> then run this again to include them.
                    </div>
                  </div>
                </div>
              )}

              {backfillPlan.sample?.length > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400 mb-2 font-semibold">First rows to update:</p>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {backfillPlan.sample.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">
                          {s.technician_name || "—"} · {s.booking_id}
                        </span>
                        <span className="text-slate-500 font-mono">
                          €{Number(s.margin ?? 0).toFixed(2)} ({Number(s.margin_pct ?? 0).toFixed(1)}%)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {backfillPlan.to_update === 0 && (
                <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-200 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Nothing to backfill — every eligible job is already computed.
                </div>
              )}
            </div>
          )}

          {backfillResult && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-100">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Backfill complete
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-emerald-200/80">Jobs updated</span>
                <span className="text-right font-mono font-bold" data-testid="jp-backfill-result-updated">{backfillResult.updated}</span>
                <span className="text-emerald-200/80">Already computed</span>
                <span className="text-right font-mono">{backfillResult.already_computed}</span>
                <span className="text-emerald-200/80">Skipped — no pay rate</span>
                <span className="text-right font-mono">{backfillResult.skipped_no_pay_rate}</span>
                <span className="text-emerald-200/80">Skipped — no material cost</span>
                <span className="text-right font-mono">{backfillResult.skipped_no_material_cost}</span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeBackfill} disabled={backfillLoading} data-testid="jp-backfill-cancel-btn" className="border-slate-700 text-slate-300">
              {backfillResult ? "Close" : "Cancel"}
            </Button>
            {backfillPlan && !backfillResult && (
              <Button
                onClick={confirmBackfill}
                disabled={backfillLoading || backfillPlan.to_update === 0}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="jp-backfill-confirm-btn"
              >
                {backfillLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                ) : backfillPlan.to_update === 0 ? (
                  "Nothing to run"
                ) : (
                  `Confirm & update ${backfillPlan.to_update} job${backfillPlan.to_update === 1 ? "" : "s"}`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Bulk confirm dialog ------------------------------------------------- */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!v && !bulkBusy) { setBulkOpen(false); setBulkPlan(null); setBulkResult(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="jp-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm: no stock materials used
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              This records a deliberate €0 material cost on each job below, so
              they start counting in the margin averages and the P&amp;L. Nothing
              is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {bulkBusy && !bulkPlan && (
            <div className="py-6 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-slate-400 text-sm">Checking those jobs…</p>
            </div>
          )}

          {(bulkResult || bulkPlan) && (
            <div className="space-y-2 text-sm max-h-72 overflow-y-auto" data-testid="jp-bulk-plan">
              {(bulkResult || bulkPlan).jobs.map((j) => (
                <div key={j.log_id}
                     className="flex justify-between gap-2 border-b border-slate-800 pb-1.5"
                     data-testid={`jp-bulk-job-${j.log_id}`}>
                  <span className="min-w-0 truncate">
                    <span className="text-white">{j.customer_name || j.booking_id}</span>
                    <span className="text-slate-500 text-xs ml-2">
                      {(j.service_names || []).join(", ")}
                    </span>
                  </span>
                  <span className={`shrink-0 text-xs ${j.status === "confirmed" || j.status === "will_confirm" ? "text-emerald-300" : "text-amber-300"}`}>
                    {j.status === "will_confirm" ? "will be confirmed"
                      : j.status === "confirmed" ? "confirmed"
                      : j.status === "has_material_lines" ? `skipped — ${j.material_line_count} material line(s)`
                      : j.status === "already_confirmed" ? "already confirmed"
                      : j.status}
                  </span>
                </div>
              ))}
              {bulkResult?.impact?.map((i) => (
                <p key={i.month} className="text-emerald-200 text-xs pt-1"
                   data-testid="jp-bulk-impact">{i.message}</p>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkPlan(null); setBulkResult(null); }}
                    disabled={bulkBusy}
                    className="border-slate-700 text-slate-300"
                    data-testid="jp-bulk-close-btn">
              {bulkResult ? "Close" : "Cancel"}
            </Button>
            {bulkPlan && !bulkResult && (
              <Button onClick={() => runBulk(false)}
                      disabled={bulkBusy || !(bulkPlan.counts?.will_confirm > 0)}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                      data-testid="jp-bulk-confirm-btn">
                {bulkBusy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>)
                  : (bulkPlan.counts?.will_confirm > 0
                    ? `Confirm ${bulkPlan.counts.will_confirm} job${bulkPlan.counts.will_confirm === 1 ? "" : "s"}`
                    : "Nothing to confirm")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {drawerLogId && (
        <JobCostDrawer
          logId={drawerLogId}
          onClose={() => setDrawerLogId(null)}
          onSaved={(res) => { setImpact(res?.impact || []); load(); }}
          stepInfo={(() => {
            // Prev/Next walks the INCOMPLETE jobs, so the owner can fix
            // all four without closing the drawer.
            const list = rowsSorted.filter((r) => r.log_id && !isComplete(r));
            const pool = list.some((r) => r.log_id === drawerLogId)
              ? list
              : rowsSorted.filter((r) => r.log_id);
            const index = pool.findIndex((r) => r.log_id === drawerLogId);
            return { index, total: pool.length,
                     hasPrev: index > 0, hasNext: index >= 0 && index < pool.length - 1,
                     pool };
          })()}
          onStep={(delta) => {
            const list = rowsSorted.filter((r) => r.log_id && !isComplete(r));
            const pool = list.some((r) => r.log_id === drawerLogId)
              ? list
              : rowsSorted.filter((r) => r.log_id);
            const index = pool.findIndex((r) => r.log_id === drawerLogId);
            const next = pool[index + delta];
            if (next) setDrawerLogId(next.log_id);
          }}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, tone, testId, hint }) => (
  <Card
    className={`bg-slate-900 border-slate-700 ${tone === "amber" ? "border-amber-500/30" : tone === "emerald" ? "border-emerald-500/30" : ""}`}
    data-testid={testId}
  >
    <CardContent className="p-4">
      <div
        className={`text-2xl font-bold tabular-nums ${tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : "text-white"}`}
      >
        {value}
      </div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{label}</div>
      {hint && (
        <div className="text-[11px] text-slate-600 mt-2" data-testid={testId ? `${testId}-hint` : undefined}>
          {hint}
        </div>
      )}
    </CardContent>
  </Card>
);

// Custom card so the promo-gap line can sit inside the same tile as the
// effective-revenue value (rather than a separate 7th tile that would
// look orphaned when no promo ran). Muted grey when gap is €0 — no
// campaign was active, nothing to explain — amber when a promo actually
// gave money away, so the line reads as informative signal not chrome.
const EffectiveRevenueCard = ({ effective, gap }) => {
  const eff = Number(effective || 0);
  const g = Number(gap || 0);
  const hasGap = g > 0.005;
  return (
    <Card className="bg-slate-900 border-slate-700" data-testid="summary-effective-revenue">
      <CardContent className="p-4">
        <div
          className="text-2xl font-bold tabular-nums text-white"
          data-testid="summary-effective-revenue-value"
        >
          €{eff.toFixed(2)}
        </div>
        <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">
          Effective revenue (ex-VAT)
        </div>
        <div
          className={`text-[11px] mt-2 ${hasGap ? "text-amber-300" : "text-slate-600"}`}
          data-testid="summary-promo-gap"
          title={hasGap
            ? "Difference between the list-price sum and what actually landed in the till — the total the shop gave away to promotions in this window."
            : "No promo discounts applied in this window."}
        >
          {hasGap
            ? `€${g.toFixed(2)} given to promos this period`
            : "€0 given to promos this period"}
        </div>
      </CardContent>
    </Card>
  );
};

export default JobProfitability;
